// Soave-Redlich-Kwong (SRK) EOS utilities.
// Minimal implementation used by src/lib/thermo/EOSK.js.

const R = 8.314462618; // J/mol/K

function cbrt(x) {
  return x < 0 ? -Math.pow(-x, 1 / 3) : Math.pow(x, 1 / 3);
}

// Solve cubic: x^3 + a*x^2 + b*x + c = 0. Return array of real roots.
function solveCubicReal(a, b, c) {
  // Convert to depressed cubic y^3 + p y + q = 0 with x = y - a/3
  const a_over3 = a / 3;
  const p = b - a * a_over3;
  const q = 2 * a_over3 * a_over3 * a_over3 - a_over3 * b + c;

  const discriminant = (q * q) / 4 + (p * p * p) / 27;

  if (discriminant > 0) {
    const sqrtD = Math.sqrt(discriminant);
    const u = cbrt(-q / 2 + sqrtD);
    const v = cbrt(-q / 2 - sqrtD);
    return [u + v - a_over3];
  }

  if (Math.abs(discriminant) <= 1e-14) {
    // Multiple roots
    const u = cbrt(-q / 2);
    return [2 * u - a_over3, -u - a_over3];
  }

  // Three real roots
  const r = Math.sqrt(-(p * p * p) / 27);
  const phi = Math.acos((-q / 2) / r);
  const t = 2 * Math.sqrt(-p / 3);
  const y1 = t * Math.cos(phi / 3);
  const y2 = t * Math.cos((phi + 2 * Math.PI) / 3);
  const y3 = t * Math.cos((phi + 4 * Math.PI) / 3);
  return [y1 - a_over3, y2 - a_over3, y3 - a_over3];
}

function alpha_SRK(T, Tc, omega) {
  const Tr = T / Tc;
  const m = 0.480 + 1.574 * omega - 0.176 * omega * omega;
  const term = 1 + m * (1 - Math.sqrt(Tr));
  return term * term;
}

export function solveSRK_mixture(P, T, x, comps, kij = null) {
  const n = comps.length;
  const ai = new Array(n);
  const bi = new Array(n);

  for (let i = 0; i < n; i++) {
    const { Tc, Pc, omega } = comps[i];
    const alpha = alpha_SRK(T, Tc, omega);
    ai[i] = 0.42747 * (R * R * Tc * Tc / Pc) * alpha;
    bi[i] = 0.08664 * (R * Tc / Pc);
  }

  const bmix = x.reduce((s, xi, i) => s + xi * bi[i], 0);

  let amix = 0;
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const kij_ij = kij && kij[i] && Number.isFinite(kij[i][j]) ? kij[i][j] : 0;
      const aij = Math.sqrt(ai[i] * ai[j]) * (1 - kij_ij);
      amix += x[i] * x[j] * aij;
    }
  }

  const A = (amix * P) / (R * R * T * T);
  const B = (bmix * P) / (R * T);

  // SRK compressibility cubic:
  // Z^3 - Z^2 + (A - B - B^2) Z - A B = 0
  const a2 = -1;
  const a1 = A - B - B * B;
  const a0 = -A * B;

  const roots = solveCubicReal(a2, a1, a0)
    .filter((z) => Number.isFinite(z))
    .sort((u, v) => u - v);

  return {
    eos: "SRK",
    P,
    T,
    x,
    comps,
    kij,
    ai,
    bi,
    amix,
    bmix,
    A,
    B,
    Z_roots: roots,
  };
}

export function computePhi_SRK(mix, Z) {
  const { x, comps, kij, ai, bi, amix, bmix, A, B } = mix;
  const n = comps.length;

  const phi = new Array(n);
  const eps = 1e-15;
  const ZmB = Math.max(Z - B, eps);
  const ln_ZmB = Math.log(ZmB);
  const ln_1pBoverZ = Math.log(1 + B / Math.max(Z, eps));

  for (let i = 0; i < n; i++) {
    const bi_over_b = bi[i] / bmix;

    // sum_j x_j a_ij
    let sum_aij = 0;
    for (let j = 0; j < n; j++) {
      const kij_ij = kij && kij[i] && Number.isFinite(kij[i][j]) ? kij[i][j] : 0;
      const aij = Math.sqrt(ai[i] * ai[j]) * (1 - kij_ij);
      sum_aij += x[j] * aij;
    }

    const term1 = bi_over_b * (Z - 1);
    const term2 = -ln_ZmB;
    const term3 = -(A / Math.max(B, eps)) * (2 * sum_aij / Math.max(amix, eps) - bi_over_b) * ln_1pBoverZ;
    const lnphi = term1 + term2 + term3;

    phi[i] = Math.exp(Math.max(-50, Math.min(50, lnphi)));
  }

  return phi;
}

// Convenience wrapper matching PR/PRSV interface used by EOSK.js.
export function solveSRK({ P, T, x, comps, kij = null }) {
  const mix = solveSRK_mixture(P, T, x, comps, kij);
  const Z_roots = mix.Z_roots || [];
  const ZL = Z_roots.length ? Math.min(...Z_roots) : mix.Z;
  const ZV = Z_roots.length ? Math.max(...Z_roots) : mix.Z;
  const phiL = computePhi_SRK(mix, ZL);
  const phiV = computePhi_SRK(mix, ZV);
  return { ZL, ZV, phiL, phiV };
}
